import React, { useRef, useEffect } from 'react';
import InfoTip from './InfoTip';

const s = {
  group: { marginBottom: '12px' },
  label: { display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  input: {
    width: '100%', padding: '8px 10px', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text-primary)',
    transition: 'border-color 0.15s', background: '#fff',
  },
  textarea: {
    width: '100%', padding: '8px 10px', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', fontSize: '13px', resize: 'vertical',
    minHeight: '90px', fontFamily: 'inherit', color: 'var(--text-primary)',
    background: '#fff',
  },
  select: {
    width: '100%', padding: '8px 10px', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', fontSize: '13px', background: '#fff',
    color: 'var(--text-primary)', cursor: 'pointer',
  },
  hint: { fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' },
  divider: { borderTop: '1px solid var(--border)', margin: '22px 0' },
  sectionTitle: { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '14px' },
};

function focusBorder(e) { e.target.style.borderColor = 'var(--accent)'; }
function blurBorder(e)  { e.target.style.borderColor = 'var(--border)'; }

// `strongLabel` (opt-in, the Memory pages) renders the label black and in
// sentence case instead of the default grey uppercase.
// `info` renders an InfoTip "i" icon after the label; `inline` lays label and
// control on one line (1/4 – 3/4). Both are opt-in — default rendering is the
// stacked label-above-input every existing caller relies on.
export const strongLabelStyle = { display: 'block', marginBottom: '5px', fontWeight: 600, fontSize: '12.5px', color: 'var(--text-primary)' };

export function Field({ label, hint, info, infoPlace, inline, children, action, strongLabel = false }) {
  const labelContent = info ? <>{label}<InfoTip text={info} place={infoPlace} /></> : label;
  const labelStyle = strongLabel ? strongLabelStyle : s.label;
  if (inline && !action) {
    return (
      <div style={{ ...s.group, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 3fr)', columnGap: '12px', alignItems: 'center' }}>
        {label ? <label style={{ ...labelStyle, marginBottom: 0 }}>{labelContent}</label> : <span />}
        <div style={{ minWidth: 0 }}>{children}</div>
        {hint && <p style={{ ...s.hint, gridColumn: '2' }}>{hint}</p>}
      </div>
    );
  }
  return (
    <div style={s.group}>
      {label && (
        action
          ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>{labelContent}</label>
              {action}
            </div>
          : <label style={labelStyle}>{labelContent}</label>
      )}
      {children}
      {hint && <p style={s.hint}>{hint}</p>}
    </div>
  );
}

export function TextInput({ value, onChange, placeholder, required, readOnly, onBlur, onKeyDown, type = 'text', maxLength }) {
  return (
    <input
      style={{ ...s.input, ...(readOnly ? { background: '#f1f5f9', color: 'var(--text-secondary)', cursor: 'default' } : {}) }}
      type={type} value={value || ''} required={required} readOnly={readOnly} maxLength={maxLength}
      placeholder={placeholder} onChange={readOnly ? undefined : (e) => onChange(e.target.value)}
      onFocus={readOnly ? undefined : focusBorder}
      onKeyDown={readOnly ? undefined : onKeyDown}
      onBlur={readOnly ? undefined : (e) => { blurBorder(e); onBlur?.(); }}
    />
  );
}

export function Textarea({ value, onChange, placeholder, rows }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [value]);
  return (
    <textarea
      ref={ref}
      style={{ ...s.textarea, minHeight: rows ? `${rows * 22}px` : '90px', resize: 'none', overflow: 'hidden' }}
      value={value || ''} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onFocus={focusBorder} onBlur={blurBorder}
    />
  );
}

export function Select({ value, onChange, options, disabled }) {
  return (
    <select
      style={{ ...s.select, ...(disabled ? { background: '#f1f5f9', color: 'var(--text-secondary)', cursor: 'default' } : {}) }}
      value={value || ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
      ))}
    </select>
  );
}

const toggleStyles = {
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' },
  label: { fontWeight: 500, fontSize: '13px', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center' },
  track: (on) => ({
    width: '38px', height: '22px', borderRadius: '99px', flexShrink: 0, cursor: 'pointer',
    background: on ? 'var(--primary)' : '#cbd5e1', position: 'relative', transition: 'background 0.2s',
    border: 'none', padding: 0,
  }),
  thumb: (on) => ({
    position: 'absolute', top: '3px', left: on ? '19px' : '3px', width: '16px', height: '16px',
    borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  }),
};

// `infoPlace` mirrors Field's — an override for the tip's preferred side. It is
// rarely needed now that InfoTip flips and clamps on its own.
export function ToggleSwitch({ checked, onChange, label, info, infoPlace }) {
  return (
    <div style={toggleStyles.row}>
      <span style={toggleStyles.label}>{label}{info ? <InfoTip text={info} place={infoPlace} /> : null}</span>
      <button type="button" role="switch" aria-checked={checked} style={toggleStyles.track(checked)} onClick={onChange}>
        <span style={toggleStyles.thumb(checked)} />
      </button>
    </div>
  );
}

const segmentStyles = {
  wrap: {
    display: 'inline-flex', width: '100%', padding: '3px', gap: '3px',
    background: '#f1f5f9', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
  },
  option: (on, disabled) => ({
    flex: 1, padding: '6px 10px', fontSize: '12.5px', fontWeight: on ? 600 : 500,
    borderRadius: '4px', border: 'none', textAlign: 'center',
    background: on ? '#fff' : 'transparent',
    color: on ? 'var(--text-primary)' : 'var(--text-secondary)',
    boxShadow: on ? 'var(--shadow-sm)' : 'none',
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
    transition: 'background 0.15s, color 0.15s',
  }),
};

/**
 * A two-or-three way either/or picker, for a choice that changes which fields
 * follow it. A <Select> hides the alternatives behind a click and reads as a
 * value; this reads as a mode, which is what such a choice is.
 * `options` are `{ value, label }`.
 */
export function SegmentedControl({ value, onChange, options, disabled }) {
  return (
    <div style={segmentStyles.wrap} role="radiogroup">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => !disabled && !on && onChange(o.value)}
            style={segmentStyles.option(on, disabled)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Divider() { return <div style={s.divider} />; }
export function SectionTitle({ children }) { return <div style={s.sectionTitle}>{children}</div>; }

export function CheckboxGroup({ options, value = [], onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => {
    const next = selected.includes(v)
      ? selected.filter((x) => x !== v)
      : [...selected, v];
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px' }}>
      {options.map((opt) => {
        const checked = selected.includes(opt.value);
        return (
          <div
            key={opt.value}
            onClick={() => toggle(opt.value)}
            style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', userSelect: 'none' }}
          >
            <span style={{
              width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
              border: `2px solid ${checked ? 'var(--accent)' : '#cbd5e1'}`,
              background: checked ? 'var(--accent)' : '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}>
              {checked && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 500 }}>{opt.label}</span>
          </div>
        );
      })}
    </div>
  );
}
